/**
 * KWC · Buy now (product page)
 * ---------------------------------------------------------------------------
 * One behaviour for every Buy now button on the product page: the one under
 * Add to cart (blocks/buy-buttons.liquid) and the sticky bar's
 * (snippets/sticky-buy-bar.liquid). Each is a [data-kwc-buy-now] button.
 *
 *   - Click: reads the currently selected variant from the product form's
 *     input[name="id"] (Horizon keeps it current), adds 1 via /cart/add.js
 *     unless that variant is already in the cart, then goes to /checkout. So
 *     going back and pressing it again never adds a second one.
 *   - While it works the button shows a spinner (aria-busy="true"). If a cart
 *     request fails, the element named by the button's aria-describedby shows
 *     "Couldn't add to cart, please try again" and the button is usable again.
 *   - Availability: mirrors the native Add to cart button. While the selected
 *     variant is unavailable every Buy now is disabled and its
 *     [data-kwc-buy-now-label] reads "Sold out"; [data-kwc-buy-now-hide-sold-out]
 *     parts (the sticky bar's price) are hidden.
 *
 * Vanilla and dependency-free. Clicks are delegated, so buttons that Horizon
 * re-renders keep working.
 */
(function () {
  var scope = document.querySelector('main[data-template="product"]') || document;

  function productForm() {
    return (
      scope.querySelector('.product-details form[data-type="add-to-cart-form"]') ||
      scope.querySelector('form[action$="/cart/add"]')
    );
  }

  function nativeAddToCart() {
    var form = productForm();
    return (form && form.querySelector('[ref="addToCartButton"]')) || scope.querySelector('[ref="addToCartButton"]');
  }

  function buyNowButtons() {
    return document.querySelectorAll('[data-kwc-buy-now]');
  }

  function errorFor(button) {
    var id = button.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  }

  function setBusy(button, busy) {
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function showError(button, show) {
    var error = errorFor(button);
    if (error) error.hidden = !show;
  }

  /* --- Availability: follow the native Add to cart button ----------------- */
  function syncAvailability() {
    var addToCart = nativeAddToCart();
    if (!addToCart) return;
    var soldOut = addToCart.disabled === true || addToCart.getAttribute('aria-disabled') === 'true';

    buyNowButtons().forEach(function (button) {
      // Only write on change: the observer below also sees these buttons.
      if (button.disabled !== soldOut) button.disabled = soldOut;
      var label = button.querySelector('[data-kwc-buy-now-label]');
      var text = soldOut ? button.dataset.soldout || 'Sold out' : button.dataset.label || 'Buy now';
      if (label && label.textContent !== text) label.textContent = text;
      button.querySelectorAll('[data-kwc-buy-now-hide-sold-out]').forEach(function (part) {
        if (part.hidden !== soldOut) part.hidden = soldOut;
      });
      if (soldOut) showError(button, false);
    });
  }

  syncAvailability();
  var form = productForm();
  if (form && 'MutationObserver' in window) {
    new MutationObserver(function (records) {
      var addToCart = nativeAddToCart();
      if (records.some(function (record) { return record.target === addToCart; })) syncAvailability();
    }).observe(form, { subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
  }

  /* --- Buy now: add once (unless already in the cart), then checkout ------- */
  function buyNow(button) {
    var currentForm = productForm();
    var idInput = currentForm && currentForm.querySelector('input[name="id"]');
    var variantId = idInput && idInput.value;

    showError(button, false);
    if (!variantId) {
      showError(button, true);
      return;
    }

    setBusy(button, true);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart read failed');
        return response.json();
      })
      .then(function (cart) {
        var inCart = (cart.items || []).some(function (item) {
          return String(item.variant_id) === String(variantId);
        });
        if (inCart) return;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        }).then(function (response) {
          if (!response.ok) throw new Error('add to cart failed');
        });
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function () {
        setBusy(button, false);
        showError(button, true);
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target instanceof Element ? event.target.closest('[data-kwc-buy-now]') : null;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    buyNow(button);
  });

  // Coming back from checkout can restore this page from the back-forward
  // cache with a button still spinning.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) buyNowButtons().forEach(function (button) { setBusy(button, false); });
  });
})();
